// Les prix sont stockés en FCFA : 1 FCFA = rate unités de la devise affichée.
export type MoneyContext = {
  currency: string;
  rate: number;
  locale: string;
};

export type MoneyRounding = "nearest" | "up" | "down";

// Devise comptable des données (montants stockés) et de l'espace admin.
export const ACCOUNTING_MONEY_CONTEXT: MoneyContext = {
  currency: "XAF",
  rate: 1,
  locale: "fr-CM",
};

// Devises débitées par carte : taux connu dans rateCurrency et deux décimales chez Stripe.
export const CARD_CURRENCIES = ["EUR", "USD"] as const;

// Décision du tech lead : la carte est débitée en euros par défaut.
export const DEFAULT_CARD_CURRENCY = "EUR";

export const resolveCardCurrency = (pricingCurrencyCode?: string | null): string => {
  const code = pricingCurrencyCode?.trim().toUpperCase();

  return CARD_CURRENCIES.find((currency) => currency === code) ?? DEFAULT_CARD_CURRENCY;
};

export const getCurrencyDecimals = (currency: string): number => {
  try {
    return (
      new Intl.NumberFormat("en", { style: "currency", currency }).resolvedOptions()
        .maximumFractionDigits ?? 2
    );
  } catch {
    return 2;
  }
};

const roundWith = (value: number, rounding: MoneyRounding) => {
  if (rounding === "up") return Math.ceil(value - 1e-9);
  if (rounding === "down") return Math.floor(value + 1e-9);

  return Math.round(value);
};

export const convertFromFcfa = (
  amountFcfa: number,
  context: MoneyContext,
  rounding: MoneyRounding = "nearest",
): number => {
  const factor = 10 ** getCurrencyDecimals(context.currency);

  return roundWith(amountFcfa * context.rate * factor, rounding) / factor;
};

export const convertToFcfa = (amount: number, context: MoneyContext): number =>
  Math.round(amount / context.rate);

export const formatMoneyValue = (value: number, context: MoneyContext): string => {
  try {
    return new Intl.NumberFormat(context.locale, {
      style: "currency",
      currency: context.currency,
    }).format(value);
  } catch {
    return `${value} ${context.currency}`;
  }
};

export const formatMoney = (
  amountFcfa: number,
  context: MoneyContext,
  rounding: MoneyRounding = "nearest",
): string => formatMoneyValue(convertFromFcfa(amountFcfa, context, rounding), context);
